/**
 * Elliance Sensor Service
 * Handles communication with Kapitani Middleware for Environmental Sensor Management
 * 
 * API Documentation: Based on IE-Middleware-Elliance
 * 
 * Available APIs:
 * 1. List of Sensors - GET /device-list
 * 2. Sensor Status - GET /device-status
 * 3. Sensor Data - GET /sensor-data
 */

import { IOT_VENDORS, KAPITANI_BASE_URL, isVendorActive } from '../config/iotVendors';

// Get Elliance config from centralized IoT vendor configuration
const ELLIANCE_VENDOR = IOT_VENDORS.ELLIANCE;

// ── sensor-data budget ───────────────────────────────────────────────────────
// The middleware ignores id/startDate/endDate, so every /sensor-data call
// returns the tenant's whole history (16.5 MB / 9,498 rows on 1 Oct 2026,
// growing ~0.5 MB a day), and pulling that repeatedly coincided with 502s and
// timeouts. Everything below exists to download it as rarely as possible and
// to keep as little of it as possible.

// The feed publishes one row every 5 minutes; a result is reused for that long
// and the page polls at the same interval.
export const SENSOR_DATA_CACHE_MS = 5 * 60 * 1000;
// Points kept per metric for charts and tables. Tiles still get the newest
// reading per metric from the whole payload.
export const SENSOR_DATA_WINDOW_DAYS = 7;
// Largest series handed to a chart.
export const CHART_MAX_POINTS = 500;

const SENSOR_DATA_TIMEOUT_MS = 60 * 1000;
const DEVICE_STATUS_TIMEOUT_MS = 20 * 1000;
const DEVICE_LIST_TIMEOUT_MS = 15 * 1000;
// Tenants remembered at once (selected farm + reference feed, with headroom
// for switching farms).
const READINGS_CACHE_MAX_KEYS = 4;

class EllianceService {
  constructor() {
    this.baseUrl = KAPITANI_BASE_URL;
    this.clientId = ELLIANCE_VENDOR.clientId;
    this.vendorId = ELLIANCE_VENDOR.vendorId;
    this.debug = process.env.REACT_APP_DEBUG_ELLIANCE === 'true' || true;
    // "farmerId|farmId" -> { promise, last, lastAt, good } — see getReadings()
    this._readingsCache = new Map();
  }

  /**
   * Check if Elliance service is configured and ready to use
   * @returns {boolean} True if vendor/client IDs are configured
   */
  isConfigured() {
    return isVendorActive('ELLIANCE');
  }

  /**
   * GET a middleware URL as JSON, giving up after `timeoutMs`. The timer
   * covers the body download as well as the headers.
   *
   * Failures are thrown as an Error carrying:
   *   kind   - 'http' | 'timeout' | 'network' | 'invalid'
   *   status - HTTP status, for kind 'http'
   *   reason - short text for the UI: "HTTP 502", "timed out after 60 s", ...
   *   serviceDown - true when the service itself is failing or asking for
   *                 fewer requests (5xx, 408, 429, timeout, network,
   *                 unreadable body) rather than rejecting this request
   *
   * @returns {Promise<{data: any, bytes: number, durationMs: number}>}
   */
  async _getJson(url, timeoutMs) {
    const controller = new AbortController();
    const startedAt = Date.now();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const fail = (kind, reason, message, status) => {
      const error = new Error(message || reason);
      error.kind = kind;
      error.reason = reason;
      error.status = status;
      error.serviceDown = kind !== 'http' || status >= 500 || status === 408 || status === 429;
      error.durationMs = Date.now() - startedAt;
      return error;
    };

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      if (!response.ok) {
        // A 5xx from the proxy is an HTML page, not JSON — never surface it.
        const body = await response.text().catch(() => '');
        let message = '';
        try { message = JSON.parse(body).message || ''; } catch (e) {}
        const reason = `HTTP ${response.status}`;
        throw fail('http', reason, message ? `${reason}: ${message}` : reason, response.status);
      }

      // Read as text so the size is known. The feed is plain ASCII, so the
      // character count is the byte count.
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw fail('invalid', 'unreadable response', 'The middleware returned a response that is not JSON');
      }
      return { data, bytes: text.length, durationMs: Date.now() - startedAt };

    } catch (error) {
      if (error.kind) throw error;
      if (error.name === 'AbortError') {
        throw fail('timeout', `timed out after ${Math.round(timeoutMs / 1000)} s`);
      }
      throw fail('network', 'network error', error.message);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * API 1: List of Sensors
   * Endpoint: GET /client/{clientId}/device-list
   * 
   * Get list of Elliance sensors from middleware database
   *
   * Gives up after 15 s, so a middleware that never answers ends as a
   * failure the page can report instead of an endless "Loading".
   * 
   * @param {string} farmId - Farm ID
   * @param {string} farmerId - Farmer ID
   * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
   */
  async listSensors(farmId, farmerId) {
    if (!this.isConfigured()) {
      return {
        success: false,
        message: 'Elliance service not configured. Please set vendorId and clientId in iotVendors.js',
        error: new Error('Service not configured')
      };
    }

    try {
      if (this.debug) {
        console.log('📋 [Elliance Service] Fetching sensor list for farm:', farmId);
      }

      const params = new URLSearchParams({
        vendorId: this.vendorId,
        farmerId: farmerId || ''
      });
      if (farmId && farmId !== 'ALL' && farmId !== 'all') {
        params.set('farmId', farmId);
      }

      const url = `${this.baseUrl}/client/${this.clientId}/device-list?${params}`;
      const { data: result } = await this._getJson(url, DEVICE_LIST_TIMEOUT_MS);

      if (this.debug) {
        console.log(`✅ [Elliance Service] Retrieved ${result?.sensors?.length || 0} sensors`);
      }

      return {
        success: true,
        data: result?.sensors || result
      };

    } catch (error) {
      console.error('❌ [Elliance Service] Error listing sensors:', error);
      return {
        success: false,
        message: error.message || 'Failed to list sensors',
        error: error
      };
    }
  }

  /**
   * API 2: Sensor Status
   * Endpoint: GET /client/{clientId}/device-status
   * 
   * Get Elliance sensor status (online/offline) from middleware database
   *
   * The route has been 404 or hanging since it was registered, so the call
   * gives up after 20 s; on a failure the page derives online / offline from
   * the farm's own readings instead (see deriveStatus in pages/Elliance.js).
   * 
   * @param {string} farmId - Farm ID
   * @param {string} farmerId - Farmer ID
   * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
   */
  async getSensorStatus(farmId, farmerId) {
    if (!this.isConfigured()) {
      return {
        success: false,
        message: 'Elliance service not configured. Please set vendorId and clientId in iotVendors.js',
        error: new Error('Service not configured')
      };
    }

    try {
      if (this.debug) {
        console.log('📊 [Elliance Service] Fetching sensor status for farm:', farmId);
      }

      const params = new URLSearchParams({
        vendorId: this.vendorId,
        farmerId: farmerId || ''
      });
      if (farmId && farmId !== 'ALL' && farmId !== 'all') {
        params.set('farmId', farmId);
      }

      const url = `${this.baseUrl}/client/${this.clientId}/device-status?${params}`;
      const { data: result } = await this._getJson(url, DEVICE_STATUS_TIMEOUT_MS);

      if (this.debug) {
        const online = result?.sensors?.filter(s => s.status === 'online').length || 0;
        const offline = result?.sensors?.filter(s => s.status === 'offline').length || 0;
        console.log(`✅ [Elliance Service] Sensor status: ${online} online, ${offline} offline`);
      }

      return {
        success: true,
        data: result.sensors || result
      };

    } catch (error) {
      console.error('❌ [Elliance Service] Error getting sensor status:', error);
      return {
        success: false,
        message: error.message || 'Failed to get sensor status',
        error: error
      };
    }
  }

  /**
   * API 3: Sensor Data
   * Endpoint: GET /client/{clientId}/sensor-data
   *
   * Returns every reading the middleware holds for a farm, as an array of
   * per-timestamp rows. Use normalizeReadings() to turn that into per-metric
   * series.
   *
   * This is the raw call and it downloads the tenant's whole history — the
   * page must go through getReadings(), which caches, de-duplicates and trims.
   *
   * Only farmerId, farmId and vendorId actually select anything. `id`,
   * `startDate` and `endDate` are part of the published contract and are
   * accepted, but the middleware ignores them — far-future, far-past and
   * malformed ranges all return the identical full set (verified Aug 2026,
   * still true Oct 2026). They are still sent so the request matches the spec,
   * and so the filtering starts working for free if Kapitani implements it;
   * filter client-side in the meantime.
   *
   * @param {Object} params - Query parameters
   * @param {string} params.farmId - Farm ID (filters)
   * @param {string} params.farmerId - Farmer ID (filters; required — a bare
   *   call returns 400 "Missing 'farmerId'")
   * @param {number} [params.sensorId] - Sensor ID (sent as `id`; ignored server-side)
   * @param {string} [params.startDate] - Start date, YYYY-MM-DD HH:mm:ss (ignored server-side)
   * @param {string} [params.endDate] - End date, YYYY-MM-DD HH:mm:ss (ignored server-side)
   * @returns {Promise<{success: boolean, data?: Array, bytes?: number, durationMs?: number, message?: string}>}
   */
  async getSensorData({ farmId, farmerId, sensorId, startDate, endDate }) {
    if (!this.isConfigured()) {
      return {
        success: false,
        message: 'Elliance service not configured. Please set vendorId and clientId in iotVendors.js',
        error: new Error('Service not configured')
      };
    }

    try {
      if (this.debug) {
        console.log(`📈 [Elliance Service] Fetching sensor data for farm ${farmId} (${startDate} to ${endDate})`);
      }

      const params = new URLSearchParams({
        vendorId: this.vendorId,
        farmerId: farmerId,
        farmId: farmId
      });
      if (sensorId !== undefined && sensorId !== null) params.set('id', String(sensorId));
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const url = `${this.baseUrl}/client/${this.clientId}/sensor-data?${params}`;
      const { data: result, bytes, durationMs } = await this._getJson(url, SENSOR_DATA_TIMEOUT_MS);

      if (this.debug) {
        const rows = Array.isArray(result) ? result.length : 0;
        console.log(`✅ [Elliance Service] Retrieved ${rows} rows (${bytes} bytes) in ${durationMs} ms`);
      }

      return {
        success: true,
        data: result,
        bytes,
        durationMs
      };

    } catch (error) {
      console.error('❌ [Elliance Service] Error getting sensor data:', error.message);
      return {
        success: false,
        message: error.message || 'Failed to get sensor data',
        error: error
      };
    }
  }

  /**
   * Shared, cached path to a farm's readings — the ONLY way the page should
   * load sensor data. One call per tenant serves the Greenhouse Monitor and
   * the Sensor Data tab alike.
   *
   *   - a settled result (good or bad) is reused for SENSOR_DATA_CACHE_MS, so
   *     a failing service is not retried more often than the normal poll;
   *   - concurrent callers share the request already in flight;
   *   - `force` (manual Refresh, the poll itself) skips the cached result but
   *     still joins a request that is already running;
   *   - the raw payload is normalised, trimmed to the last
   *     SENSOR_DATA_WINDOW_DAYS days and then dropped — only the trimmed
   *     series and a short summary are kept;
   *   - the last good result is remembered, and handed back as `lastGood`
   *     when a later call fails.
   *
   * Never rejects.
   *
   * @param {Object} params
   * @param {string} params.farmerId
   * @param {string} params.farmId
   * @param {boolean} [params.force=false]
   * @returns {Promise<Object>} on success
   *   { success: true, readings, summary, request, loadedAt: Date, fromCache, shared? }
   *   on failure (HTTP error, timeout, network error, or an HTTP-200 body
   *   that is not a list of rows)
   *   { success: false, message, reason, kind, status, serviceDown, request,
   *     durationMs, failedAt: Date, lastGood: {readings, summary, loadedAt}|null, fromCache, shared? }
   */
  getReadings({ farmerId, farmId, force = false }) {
    const key = `${farmerId}|${farmId}`;
    const slot = this._readingsCache.get(key) || { promise: null, last: null, lastAt: 0, good: null };
    // Re-insert on every use so the Map stays in least-recently-used order.
    this._readingsCache.delete(key);
    this._readingsCache.set(key, slot);
    this._trimReadingsCache(key);

    // `shared` marks a caller that joined someone else's request, so that only
    // the caller that started it records it in the API log.
    if (slot.promise) return slot.promise.then(result => ({ ...result, shared: true }));
    if (!force && slot.last && Date.now() - slot.lastAt < SENSOR_DATA_CACHE_MS) {
      return Promise.resolve({ ...slot.last, lastGood: slot.good, fromCache: true });
    }

    slot.promise = this._loadReadings({ farmerId, farmId })
      .catch(error => ({
        // Only reachable if normalising throws on a payload of an unexpected shape.
        success: false,
        message: error.message || 'Failed to read sensor data',
        reason: 'unreadable response',
        kind: 'invalid',
        serviceDown: true,
        request: { farmerId, farmId },
        failedAt: new Date()
      }))
      .then(result => {
        slot.last = result;
        slot.lastAt = Date.now();
        if (result.success) {
          slot.good = { readings: result.readings, summary: result.summary, loadedAt: result.loadedAt };
        }
        return { ...result, lastGood: slot.good, fromCache: false };
      })
      .finally(() => { slot.promise = null; });
    return slot.promise;
  }

  /**
   * The last good result for a tenant, without touching the network.
   * @returns {{readings: Object, summary: Object, loadedAt: Date}|null}
   */
  peekReadings({ farmerId, farmId }) {
    const key = `${farmerId}|${farmId}`;
    const slot = this._readingsCache.get(key);
    if (!slot || !slot.good) return null;
    // Being shown counts as being used: keep it at the recent end of the Map.
    this._readingsCache.delete(key);
    this._readingsCache.set(key, slot);
    return slot.good;
  }

  // Forget tenants once the cache is over budget: first the ones holding no
  // readings (failed or empty results - cheap to ask again), least recently
  // used first, and only then tenants whose last good data would be lost.
  // Without that order, switching farms during an outage pushed out the
  // reference feed's last good data, the only thing left to show.
  _trimReadingsCache(keepKey) {
    if (this._readingsCache.size <= READINGS_CACHE_MAX_KEYS) return;
    const holdsData = (slot) => Boolean(slot.good && slot.good.readings.rows > 0);
    const evictable = [...this._readingsCache].filter(([key, slot]) => key !== keepKey && !slot.promise);
    const ordered = [
      ...evictable.filter(([, slot]) => !holdsData(slot)),
      ...evictable.filter(([, slot]) => holdsData(slot))
    ];
    for (const [key] of ordered) {
      if (this._readingsCache.size <= READINGS_CACHE_MAX_KEYS) break;
      this._readingsCache.delete(key);
    }
  }

  // One network round-trip for getReadings(): fetch, normalise, trim, summarise.
  async _loadReadings({ farmerId, farmId }) {
    const now = new Date();
    const since = new Date(now.getTime() - SENSOR_DATA_WINDOW_DAYS * 24 * 60 * 60 * 1000);
    const request = {
      farmerId,
      farmId,
      startDate: this.formatFeedDate(since),
      endDate: this.formatFeedDate(now)
    };

    const result = await this.getSensorData(request);
    if (!result.success) {
      const error = result.error || {};
      return {
        success: false,
        message: result.message,
        reason: error.reason || result.message,
        kind: error.kind || 'config',
        status: error.status,
        serviceDown: Boolean(error.serviceDown),
        durationMs: error.durationMs,
        request,
        failedAt: new Date()
      };
    }

    // HTTP 200 with a body that is not the list of rows - the middleware
    // answers some bad requests that way, e.g. {"message":"Missing 'vendorId'"}.
    // That is a failed request, not "this tenant has no readings": reporting
    // it as an empty success would replace the last good data with nothing.
    // It is a rejection rather than an outage, so serviceDown stays false.
    if (!Array.isArray(result.data)) {
      const vendorMessage = result.data && typeof result.data === 'object' && typeof result.data.message === 'string'
        ? result.data.message.slice(0, 200)
        : '';
      return {
        success: false,
        message: vendorMessage
          ? `Unexpected response: ${vendorMessage}`
          : 'The middleware returned a response that is not a list of readings',
        reason: 'unreadable response',
        detail: vendorMessage || null,
        kind: 'invalid',
        status: 200,
        serviceDown: false,
        durationMs: result.durationMs,
        request,
        failedAt: new Date()
      };
    }

    // `result.data` is the only reference to the raw payload; it goes out of
    // scope with this function, so nothing downstream can hold on to it.
    const readings = this.normalizeReadings(result.data, { since });
    return {
      success: true,
      readings,
      summary: {
        rows: readings.rows,
        points: readings.points,
        keptPoints: readings.keptPoints,
        bytes: result.bytes,
        durationMs: result.durationMs,
        oldest: readings.oldestAt ? this.formatFeedDate(readings.oldestAt) : null,
        newest: readings.latestAt ? this.formatFeedDate(readings.latestAt) : null,
        windowDays: SENSOR_DATA_WINDOW_DAYS
      },
      request,
      loadedAt: new Date()
    };
  }

  /**
   * Utility: Get sensor summary statistics
   * 
   * @param {Array} sensors - Array of sensor objects with status
   * @returns {Object} Summary statistics
   */
  getSensorSummary(sensors) {
    if (!Array.isArray(sensors)) return { total: 0, online: 0, offline: 0 };
    
    return {
      total: sensors.length,
      online: sensors.filter(s => s.status === 'online').length,
      offline: sensors.filter(s => s.status === 'offline').length
    };
  }

  /**
   * Utility: Parse a feed timestamp ("YYYY-MM-DD HH:mm:ss", no zone).
   *
   * The feed's clock is UTC: a payload fetched at 12:28:31 UTC on 1 Oct 2026
   * ended at "2026-10-01 12:25:05", i.e. the 5-minute slot just before the
   * request — read as local (UTC+8) time that reading would have been eight
   * hours old on a feed that publishes every five minutes. A value that does
   * carry a zone designator is taken at its word.
   *
   * @param {string} raw
   * @returns {Date} Invalid Date when the value cannot be parsed
   */
  parseFeedTimestamp(raw) {
    const iso = String(raw).trim().replace(' ', 'T');
    const zoned = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
    return new Date(zoned ? iso : `${iso}Z`);
  }

  /**
   * Utility: Format a Date the way the feed (and the startDate/endDate
   * contract) writes one — "YYYY-MM-DD HH:mm:ss", UTC.
   *
   * @param {Date} date
   * @returns {string}
   */
  formatFeedDate(date) {
    return date.toISOString().slice(0, 19).replace('T', ' ');
  }

  /**
   * Utility: Normalise a raw /sensor-data payload into per-metric series.
   *
   * The middleware returns one row per timestamp, each row carrying the
   * metrics that were sampled at that instant:
   *
   *   [{ farmId, farmerId, "Water pH": { id, timestamp, value, sensor_type } }, ...]
   *
   * The published PDF documents an inverted object-of-arrays shape
   * (`{ "Water_pH": [{ value, timestamp }, ...] }`) that the middleware has
   * never actually produced — trust the array.
   *
   * `sensor_type` is deliberately dropped. Its labels are shifted by one group
   * on some tenants (Soil pH arrives tagged "Ambient Sensor 1", PAR as
   * "NPK Sensor 1"), so the metric key is the only trustworthy identifier.
   *
   * With `since`, the series keep only readings at or after that instant while
   * `latest`, `latestAt` and `oldestAt` still describe the whole payload — the
   * tiles and the feed's date range stay truthful, and the 170,000-odd point
   * objects the full history would need are never created.
   *
   * @param {Array} payload - Raw response from getSensorData()
   * @param {Object} [options]
   * @param {Date|number} [options.since] - Drop series points older than this
   * @returns {{series: Object, latest: Object, latestAt: Date|null, oldestAt: Date|null,
   *            rows: number, points: number, keptPoints: number, since: Date|null}}
   *   series     - metric key -> [{ timestamp: Date, value: number }] ascending
   *   latest     - metric key -> most recent { timestamp, value }
   *   latestAt   - newest timestamp anywhere in the payload
   *   oldestAt   - oldest timestamp anywhere in the payload
   *   points     - valid readings in the payload
   *   keptPoints - readings kept in `series`
   */
  normalizeReadings(payload, { since = null } = {}) {
    const series = {};
    const rows = Array.isArray(payload) ? payload : [];
    const sinceMs = since === null || since === undefined ? -Infinity : Number(since);

    const latestMs = {};
    const latestValue = {};
    let newestMs = -Infinity;
    let oldestMs = Infinity;
    let points = 0;
    let keptPoints = 0;
    // Every metric in a row shares the row's timestamp, so remembering the
    // last string parsed saves ~17 of every 18 Date parses.
    let lastRaw = null;
    let lastMs = NaN;

    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      for (const metric of Object.keys(row)) {
        const reading = row[metric];
        if (!reading || typeof reading !== 'object') continue; // skips farmId/farmerId
        const value = parseFloat(reading.value);
        if (reading.timestamp !== lastRaw) {
          lastRaw = reading.timestamp;
          lastMs = this.parseFeedTimestamp(lastRaw).getTime();
        }
        const ms = lastMs;
        if (Number.isNaN(value) || Number.isNaN(ms)) continue;

        points++;
        if (ms > newestMs) newestMs = ms;
        if (ms < oldestMs) oldestMs = ms;
        if (!(latestMs[metric] > ms)) {
          latestMs[metric] = ms;
          latestValue[metric] = value;
        }
        if (ms >= sinceMs) {
          (series[metric] || (series[metric] = [])).push({ timestamp: new Date(ms), value });
          keptPoints++;
        }
      }
    }

    for (const pts of Object.values(series)) {
      pts.sort((a, b) => a.timestamp - b.timestamp);
    }

    const latest = {};
    for (const metric of Object.keys(latestMs)) {
      latest[metric] = { timestamp: new Date(latestMs[metric]), value: latestValue[metric] };
    }

    return {
      series,
      latest,
      latestAt: points ? new Date(newestMs) : null,
      oldestAt: points ? new Date(oldestMs) : null,
      rows: rows.length,
      points,
      keptPoints,
      since: sinceMs === -Infinity ? null : new Date(sinceMs)
    };
  }

  /**
   * Utility: Thin a series to at most `maxPoints` by taking evenly spaced
   * points. The first and the newest point are always kept. A series already
   * within the limit is returned as is.
   *
   * @param {Array} points - Ascending series
   * @param {number} [maxPoints=CHART_MAX_POINTS]
   * @returns {Array}
   */
  downsample(points, maxPoints = CHART_MAX_POINTS) {
    if (!Array.isArray(points)) return [];
    if (points.length <= maxPoints || maxPoints < 2) return points;
    const last = points.length - 1;
    const step = last / (maxPoints - 1);
    const out = new Array(maxPoints);
    for (let i = 0; i < maxPoints; i++) out[i] = points[Math.round(i * step)];
    out[maxPoints - 1] = points[last];
    return out;
  }

  /**
   * Utility: Format one metric's readings for charting.
   *
   * @param {Array|Object} data - Raw payload from getSensorData(), or an
   *   already-normalised series map
   * @param {string} metricKey - Middleware metric key, e.g. 'Water pH', 'PAR'
   * @returns {Array} [{ x: Date, y: number }] ascending by time, at most
   *   CHART_MAX_POINTS points
   */
  formatChartData(data, metricKey) {
    const series = Array.isArray(data) ? this.normalizeReadings(data).series : (data || {});
    const points = series[metricKey];
    if (!Array.isArray(points)) return [];
    return this.downsample(points).map(p => ({ x: p.timestamp, y: p.value }));
  }
}

export default new EllianceService();
